/**
 * Der Bericht als Vorschlag (Layout S3 „Beweiskette“, seit 09.10.2026, von
 * Robert im Canvas gewählt).
 *
 * ANLASS: Der Kunde kennt die Arbeitssession nicht. Der Bericht zeigt deshalb
 * zuerst den Vorschlag (neue Texte, neues Design, hier ist er) und danach die
 * Belege in fester Reihenfolge: Text-Simulation, Design-Simulation,
 * Fachprüfer. Der Bericht erscheint so nur, wenn der Snapshot `vorschlag`
 * trägt (publish_report, bericht_vorschlag.ts auf dem Server); sonst bleibt
 * Layout A.
 *
 * Hier steht nur Rechnung, keine Darstellung, alles aus der eingefrorenen
 * Nutzlast. Fehlt etwas, liefert die Funktion null oder eine leere Liste, und
 * die Seite lässt den Teil samt Sprunglink weg. Keine Zahl wird geschätzt.
 */

import { anzahlUrteile, personaNoten } from './bericht-noten';
import { leseKernRand } from './kern-rand';
import { leseAbschnitte, type Stimme } from './befunde';
import { buendleEinwaende } from './bericht-auswertung';
import { kernRandZahl, standDer } from './bericht-blatt';
import { KRITERIEN } from './bericht-ablauf';
import { sichererLink } from './sicherer-link';
import { locale, sprache, t } from './sprache';

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const istZahl = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const ganz = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);
const rund = (n: number) => Math.round(n * 10) / 10;
export const zahl1 = (n: number) => new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
/** „+1,8“, „−0,3“, „±0,0“, mit echtem Minuszeichen. */
export const vorzeichen = (d: number) => `${d > 0 ? '+' : d < 0 ? '−' : '±'}${zahl1(Math.abs(d))}`;
const ZAHLWORT = ['Kein', 'Ein', 'Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben', 'Acht', 'Neun', 'Zehn', 'Elf', 'Zwölf'];
const ZAHLWORT_EN = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];
const zahlwort = (n: number) => t(ZAHLWORT, ZAHLWORT_EN)[n] ?? String(n);
/** „a“, „a und b“, „a, b und c“. */
const aufzaehlung = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} ${t('und', 'and')} ${xs[xs.length - 1]}`);
const gross = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Der Vorschlag ───────────────────────────────────────────────────────────

export interface Aenderung { bereich: string; heute: string; neu: string; kollidiert_mit: string }
export interface Vorschlag {
  titel: string;
  text: string;
  entwurf_url: string | null;
  texte_url: string | null;
  aenderungen: Aenderung[];
}

/**
 * Der Vorschlag aus dem Snapshot, oder null (dann Layout A). Ohne Titel kein
 * Vorschlag: er ist die Überschrift. Adressen nur http(s), wie jede Adresse
 * aus Eingaben im Bericht.
 */
export function leseVorschlag(data: any): Vorschlag | null {
  const v = data?.vorschlag;
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const titel = text(v.titel);
  if (!titel) return null;
  return {
    titel,
    text: text(v.text),
    entwurf_url: sichererLink(v.entwurf_url),
    texte_url: sichererLink(v.texte_url),
    aenderungen: (Array.isArray(v.aenderungen) ? v.aenderungen : [])
      .map((a: any) => ({ bereich: text(a?.bereich), heute: text(a?.heute), neu: text(a?.neu), kollidiert_mit: text(a?.kollidiert_mit) }))
      .filter((a: Aenderung) => a.bereich && a.neu),
  };
}

// ─── Bilder: heute und neu ───────────────────────────────────────────────────

export interface Seitenbilder { desktop: string | null; mobil: string | null }

function bilderVon(links: any): Seitenbilder | null {
  const desktop = sichererLink(links?.desktop);
  const mobil = sichererLink(links?.mobil);
  return desktop || mobil ? { desktop, mobil } : null;
}

/** Die beiden Checks im Vergleich, wie der Snapshot sie trägt. */
export function vergleichsChecks(data: any): { heute: any | null; neu: any | null } {
  const v = data?.design_vergleich;
  const ok = (x: any) => (x && typeof x === 'object' ? x : null);
  return { heute: ok(v?.heute), neu: ok(v?.neu) };
}

/**
 * Bilder der heutigen Seite und der neuen Fassung (neu signiert bei jedem
 * Abruf). Die neue Fassung kommt aus dem Check „neu“; ohne ihn nur aus einem
 * Check der Studie, der selbst eine neue Fassung ist (Skizze oder Entwurf).
 * Nie aus `gepruefteSeite`: die nimmt den jüngsten Check und bei einem Paar
 * ausdrücklich die heutige Seite, und dann stünde die Live-Seite unter „Unser
 * Vorschlag“ (Befund der Prüfung vom 09.10.2026). Ohne solchen Check: null, der
 * Hero steht ohne Bild.
 */
export function vergleichsBilder(data: any): { heute: Seitenbilder | null; neu: Seitenbilder | null } {
  const { heute, neu } = vergleichsChecks(data);
  const ersatz = neu ? null : (Array.isArray(data?.design) ? data.design : [])
    .find((d: any) => (d?.skizze || d?.quelle === 'html' || d?.quelle === 'bilder') && bilderVon(d?.bild_links));
  return {
    heute: bilderVon(heute?.bild_links),
    neu: bilderVon((neu ?? ersatz)?.bild_links),
  };
}

/**
 * Was die neue Fassung im Design-Check war: eine Skizze (neuer Text in der
 * heutigen Gestaltung), ein Entwurf (HTML oder Bilder) oder null (eine Seite
 * unter ihrer Adresse). Eine Skizze ist keine Gestaltung, und das sagt der
 * Bericht überall, wo er sie zeigt.
 */
export function artDerNeuen(data: any): 'skizze' | 'entwurf' | null {
  const { neu } = vergleichsChecks(data);
  if (!neu) return null;
  if (neu.skizze) return 'skizze';
  return neu.quelle === 'html' || neu.quelle === 'bilder' ? 'entwurf' : null;
}

export const SKIZZE_SATZ = 'Skizze: neuer Text in der heutigen Gestaltung, kein neues Design.';
export const SKIZZE_SATZ_EN = 'Sketch: new copy in the current design, no new design.';

// ─── Beleg 1: Text-Simulation ────────────────────────────────────────────────

export interface Teilgruppe { n: number; dafuer: number; heute: number | null; neu: number | null }

export interface TextBeleg {
  art: 'vergleich' | 'feedback';
  dafuer: number;
  gesamt: number;
  /** Dativ für „x von y …“: Kern-Urteilen, Urteilen, Kern-Personas, Personas (Englisch: core verdicts, verdicts, …). */
  einheit: string;
  /** „heute“, nur wenn belegt ist, dass die Vergleichsfassung die heutige Seite war; sonst „Vergleich“ (Englisch: current, baseline). */
  altLabel: string;
  /** Unabhängig von der Sprache: war die Vergleichsfassung die heutige Seite, zählt nur der Kern, urteilen Personas mehrmals? */
  istHeute: boolean;
  kern: boolean;
  matrix: boolean;
  /** Wogegen gelesen wurde, im Satz: „die heutige Seite“, „F2 vom 05.10.“, „eure Vergleichsfassung“. */
  gegen: string;
  heute: number | null;
  neu: number | null;
  /** Die Überschrift des Belegs, z. B. „34 von 35 Kern-Urteilen sprechen für die neue Fassung“. */
  titel: string;
  /** Das Etikett daneben: besser, etwa gleich, heute vorn. */
  etikett: string;
  bestanden: boolean;
  /** Spricht der Beleg gegen die neue Fassung (die Vergleichsfassung vorn, die Mehrheit zögert)? */
  dagegen: boolean;
  /** Mit Kern und Rand: alle Urteile und der Rand für sich. */
  alle: Teilgruppe | null;
  rand: Teilgruppe | null;
  /** Verschiedene Personas und Urteile im gezeigten Run. */
  personas: number;
  urteile: number;
}

function alleUrteile(v: any): Teilgruppe | null {
  const zeilen = (Array.isArray(v?.personen) ? v.personen : []).filter((p: any) => istZahl(p?.geprueft) && istZahl(p?.vergleich));
  if (!zeilen.length) return null;
  const mittel = (k: 'geprueft' | 'vergleich') => rund(zeilen.reduce((s: number, p: any) => s + p[k], 0) / zeilen.length);
  return { n: zeilen.length, dafuer: zeilen.filter((p: any) => p.gewinner === 'geprueft').length, heute: mittel('vergleich'), neu: mittel('geprueft') };
}

/**
 * War die Vergleichsfassung die heutige Seite? Nur, wenn es belegt ist: der
 * Autor sagt es in `narrative.vergleichsfassung` („die heutige Seite“), oder
 * der Run kommt aus dem Seitentest (A ist dort die geladene Live-Seite, der
 * Name beginnt mit „A/B-Test:“, ältere heißen `seite-<fokus>`). Ein Run per
 * simulate_copy gegen eine frühere Fassung ist nicht die heutige Seite, auch
 * wenn der Nutzer die Vergleichsfassung selbst gesetzt hat.
 */
export function vergleichIstHeute(data: any, v: any): boolean {
  const vf = text(data?.narrative?.vergleichsfassung);
  if (vf) return /heutig|live|aktuell|bestehend|current|existing/i.test(vf);
  if (!v || v.baseline_source === 'generated') return false;
  return /^(A\/B-Test:|seite-)/i.test(text(v.label));
}

/**
 * Die Vergleichsfassung im Satz, für die Warnung „überzeugt vor allem die
 * erweiterte Zielgruppe“ (`randWarnung` in kern-rand.ts), im Akkusativ: „die heutige
 * Seite“, der Name des Autors aus `narrative.vergleichsfassung`, sonst „die
 * Vergleichsfassung“. Ohne `data` nur, was am Run selbst steht.
 */
export function vergleichFuerSatz(data: any, v: any): string {
  if (vergleichIstHeute(data, v)) return t('die heutige Seite', 'the current page');
  return text(data?.narrative?.vergleichsfassung) || t('die Vergleichsfassung', 'the baseline');
}

/** Wogegen die neue Fassung im Text-Vergleich antrat, im Satz. */
function gegenSatz(data: any, v: any, heute: boolean): string {
  if (heute) return t('die heutige Seite', 'the current page');
  const vf = text(data?.narrative?.vergleichsfassung);
  if (vf) return vf;
  return v?.baseline_source === 'generated'
    ? t('eine von 99takes erzeugte Vergleichsfassung', 'a baseline generated by 99takes')
    : t('eure Vergleichsfassung', 'your baseline');
}

/** Die Text-Simulation aus der letzten Fassung des Berichts, oder null. */
export function textBeleg(data: any): TextBeleg | null {
  const v = standDer(data);
  if (!v) return null;
  const dafuer = ganz(v.wins?.candidate);
  const gesamt = ganz(v.wins?.total);
  const personas = personaNoten(v).length;
  const urteile = anzahlUrteile(v);
  if (v.mode === 'feedback') {
    if (!gesamt) return null;
    const mehrheit = dafuer * 2 > gesamt;
    return {
      art: 'feedback', dafuer, gesamt, einheit: t('Personas', 'personas'), altLabel: '', gegen: '', heute: null, neu: null,
      istHeute: false, kern: false, matrix: false,
      titel: t(`${dafuer} von ${gesamt} Personas würden den nächsten Schritt gehen`, `${dafuer} of ${gesamt} personas would take the next step`),
      etikett: mehrheit ? t('Mehrheit geht weiter', 'Majority moves on') : t('Mehrheit zögert', 'Majority hesitates'), bestanden: mehrheit, dagegen: !mehrheit,
      alle: null, rand: null, personas, urteile,
    };
  }
  const heute = istZahl(v.scores?.baseline) ? v.scores.baseline : null;
  const neu = istZahl(v.scores?.candidate) ? v.scores.candidate : null;
  if (!gesamt && heute == null && neu == null) return null;
  const kr = leseKernRand(v);
  const matrix = urteile > personas;
  const einheit = sprache() === 'en'
    ? (matrix ? (kr ? 'core verdicts' : 'verdicts') : (kr ? 'core personas' : 'personas'))
    : (matrix ? (kr ? 'Kern-Urteilen' : 'Urteilen') : (kr ? 'Kern-Personas' : 'Personas'));
  const istHeute = vergleichIstHeute(data, v);
  const altLabel = istHeute ? t('heute', 'current') : t('Vergleich', 'baseline');
  // Nur das Urteil der Engine (Rauschschwelle des Runs), keine eigene Stufe
  // wie „klar besser“ ab einem festen Notenabstand.
  const etikett = v.verdict === 'candidate' ? t('besser', 'better')
    : v.verdict === 'baseline' ? t(`${altLabel} vorn`, `${altLabel} ahead`) : t('etwa gleich', 'about the same');
  return {
    art: 'vergleich', dafuer, gesamt, einheit, altLabel, gegen: gegenSatz(data, v, istHeute), heute, neu,
    istHeute, kern: !!kr, matrix,
    titel: t(
      `${dafuer} von ${gesamt} ${einheit} ${matrix ? 'sprechen für die neue Fassung' : 'ziehen die neue Fassung vor'}`,
      `${dafuer} of ${gesamt} ${einheit} ${matrix ? 'favor the new version' : 'prefer the new version'}`,
    ),
    etikett,
    bestanden: v.verdict === 'candidate',
    dagegen: v.verdict === 'baseline',
    alle: kr ? alleUrteile(v) : null,
    rand: kr ? { n: kr.rand.n, dafuer: kr.rand.wins.candidate, heute: kr.rand.scores.baseline, neu: kr.rand.scores.candidate } : null,
    personas,
    urteile,
  };
}

export interface WirktZeile { abschnitt: string; n: number; von: number; kern: boolean }

/**
 * Die Abschnitte der neuen Fassung, die tragen: dort wirkt mehr, als bremst und
 * fehlt zusammen (dieselbe Regel wie „Trägt“ im Layout A), im Kern, wenn es ihn
 * gibt. Die häufigsten zuerst, höchstens zwei. Ein Abschnitt, der zugleich am
 * meisten bremst, steht hier nicht als Stärke.
 */
export function wasWirkt(data: any, max = 2): WirktZeile[] {
  const v = standDer(data);
  const roh = v?.mode === 'feedback' ? v?.abschnitte : v?.abschnitte?.candidate;
  return leseAbschnitte(roh)
    .map((a) => {
      const kern = a.kern_von != null && a.kern_von > 0;
      const zahl = (b: { n: number; kern?: number }) => (kern ? (b.kern ?? 0) : b.n);
      return { abschnitt: a.abschnitt, n: zahl(a.wirkt), von: kern ? a.kern_von! : a.von, kern, gegen: zahl(a.bremst) + zahl(a.fehlt) };
    })
    .filter((z) => z.n > 0 && z.n > z.gegen)
    .map(({ gegen: _g, ...z }) => z)
    .sort((a, b) => b.n / b.von - a.n / a.von || b.n - a.n)
    .slice(0, max);
}

/**
 * Typische Aussagen über die neue Fassung im Wortlaut, nie vom Autor: zuerst
 * die Zitate unter „Was zieht“, die der Server aus dem Run gewählt hat, dann
 * je Abschnitt, was wirkt. Höchstens drei, eine je Persona, und keine, die
 * eine andere enthält („Genau das ist uns passiert.“ steckt oft wörtlich in
 * einem längeren Satz derselben Persona).
 */
export function stimmenNeu(data: any, max = 3): Stimme[] {
  const out: Stimme[] = [];
  const norm = (t: string) => t.toLowerCase().replace(/[^a-zäöüß0-9]+/g, ' ').trim();
  const nimm = (s: Stimme) => {
    if (!s.text) return;
    const a = norm(s.text);
    const doppelt = out.some((x) => {
      const b = norm(x.text);
      return a.includes(b) || b.includes(a) || (!!s.persona && x.persona === s.persona);
    });
    if (!doppelt) out.push(s);
  };
  for (const h of Array.isArray(data?.narrative?.hebel) ? data.narrative.hebel : []) {
    if (text(h?.zitat?.text)) nimm({ text: text(h.zitat.text), persona: text(h.zitat.persona) });
  }
  const v = standDer(data);
  const roh = v?.mode === 'feedback' ? v?.abschnitte : v?.abschnitte?.candidate;
  const abschnitte = leseAbschnitte(roh).sort((a, b) => b.wirkt.n - a.wirkt.n);
  for (const a of abschnitte) if (a.wirkt.stimmen[0]) nimm(a.wirkt.stimmen[0]);
  return out.slice(0, max);
}

/** Eine Aussage über die heutige Seite: was dort am häufigsten bremst. Nur, wenn belegt ist, dass die Vergleichsfassung die heutige Seite war. */
export function stimmeHeute(data: any): Stimme | null {
  const v = standDer(data);
  if (!v || v.mode === 'feedback' || !vergleichIstHeute(data, v)) return null;
  const a = leseAbschnitte(v.abschnitte?.baseline).filter((x) => x.bremst.stimmen[0]).sort((x, y) => y.bremst.n - x.bremst.n)[0];
  return a ? a.bremst.stimmen[0]! : null;
}

export interface Stoert { einwand: string; n: number; von: number; /** Gezählt im Kern (ohne Randpersonas). */ kern: boolean }

/**
 * Was an der neuen Fassung noch stört: die Einwände der letzten Fassung,
 * gebündelt, mit Zahl der Personas. Höchstens drei. Mit Kern und Rand nur der
 * Kern, Zähler und Nenner, wie der Rest von Beleg 1 („x von 13 Kern-Personas“);
 * vorher standen dort alle 18 als Nenner neben Kern-Zahlen.
 */
export function stoertNoch(data: any, max = 3): Stoert[] {
  const v = standDer(data);
  if (!v) return [];
  const kr = kernRandZahl(data);
  if (kr) {
    const nurKern = { ...v, blockers: (Array.isArray(v.blockers) ? v.blockers : []).filter((b: any) => b?.rolle !== 'rand') };
    return buendleEinwaende([nurKern]).slice(0, max).map((g) => ({ einwand: g.einwand, n: g.personas.length, von: kr.kern, kern: true }));
  }
  const von = personaNoten(v).length || ganz(v.wins?.total);
  return buendleEinwaende([v])
    .filter((g) => !g.nurRand)
    .slice(0, max)
    .map((g) => ({ einwand: g.einwand, n: g.personas.length, von, kern: false }));
}

// ─── Beleg 2: Design-Simulation ──────────────────────────────────────────────

/**
 * Annahme, wenn der Bericht die Streuung nicht trägt: einzelne Noten einer
 * Persona streuen um etwa einen Punkt (Standardabweichung). Nicht gemessen;
 * seit 09.10.2026 friert publish_report die gemessene Streuung je Check ein
 * (`streuung` in design_vergleich, bericht_design.ts), und dann gilt sie. Die
 * Seite sagt, welche der beiden gilt.
 */
export const STREUUNG_EINZELNOTE = 1;
/** So viele Personas hat ein Design-Check, wenn der Bericht es nicht sagt. */
export const PERSONAS_VORGABE = 8;

/** Die Streuung eines Checks, wie publish_report sie einfriert: gepoolt über die Kriterien, mit Freiheitsgraden. */
export interface Streuung { s: number; df: number }

const streuungVon = (d: any): Streuung | null => {
  const s = d?.streuung;
  return s && istZahl(s.s) && s.s >= 0 && istZahl(s.df) && s.df >= 1 ? { s: s.s, df: s.df } : null;
};

/** Die gemeinsame Streuung zweier Checks (gepoolte Varianz), oder null, wenn einer sie nicht trägt. */
export function gepoolteStreuung(a: Streuung | null, b: Streuung | null): number | null {
  if (!a || !b) return null;
  return Math.sqrt((a.df * a.s * a.s + b.df * b.s * b.s) / (a.df + b.df));
}

/**
 * Bis zu welchem Abstand zwei Noten aus getrennten Checks „etwa gleich“ sind.
 *
 * Jede Note ist ein Mittel über n Personas. Streuen die Einzelnoten um s, hat
 * das Mittel den Standardfehler s / √n, und der Abstand zweier unabhängiger
 * Mittel s · √(1/n₁ + 1/n₂). Bis zu einem Standardfehler gilt der Abstand als
 * Rauschen, ab zwei als deutlich (rund 95 % bei Normalverteilung), dazwischen
 * als Tendenz. Mit s = 1 Punkt und 8 Personas je Check: √(2/8) = 0,5. Mit 4 je
 * Check 0,7, mit 18 je Check 0,3. Gerundet auf eine Stelle (so stehen die
 * Noten da), mindestens 0,2. `s` ist die gemessene Streuung, sonst die Annahme.
 */
export function rauschgrenze(n1?: unknown, n2?: unknown, s: number | null = null): number {
  const n = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 1 ? x : PERSONAS_VORGABE);
  return Math.max(0.2, rund((s ?? STREUUNG_EINZELNOTE) * Math.sqrt(1 / n(n1) + 1 / n(n2))));
}

export type Richtung = 'deutlich-besser' | 'besser' | 'gleich' | 'schlechter' | 'deutlich-schlechter';

const EPS = 1e-9;
export function richtung(abstand: number, grenze: number): Richtung {
  if (Math.abs(abstand) <= grenze + EPS) return 'gleich';
  const deutlich = Math.abs(abstand) >= 2 * grenze - EPS;
  return abstand > 0 ? (deutlich ? 'deutlich-besser' : 'besser') : (deutlich ? 'deutlich-schlechter' : 'schlechter');
}

/** Wie ein Kriterium in der Tabelle, im Satz („am Handy“) und auf einem Etikett heißt. */
export const KRITERIUM_KURZ: Record<string, string> = {
  handy: 'Handy', erster_eindruck: 'Erster Eindruck', passt_zu_mir: 'Passt zu mir', glaubwuerdig: 'Seriös', orientierung: 'Orientierung',
};
export const KRITERIUM_KURZ_EN: Record<string, string> = {
  handy: 'Mobile', erster_eindruck: 'First impression', passt_zu_mir: 'Fits me', glaubwuerdig: 'Credible', orientierung: 'Orientation',
};
/** Die Kriterien in der aktuellen Sprache. */
export const kriteriumKurz = (): Record<string, string> => t(KRITERIUM_KURZ, KRITERIUM_KURZ_EN);
const IM_SATZ_DE: Record<string, string> = {
  handy: 'am Handy', erster_eindruck: 'beim ersten Eindruck', passt_zu_mir: 'bei „passt zu mir“', glaubwuerdig: 'bei „seriös“', orientierung: 'bei der Orientierung',
};
const IM_SATZ_EN: Record<string, string> = {
  handy: 'on mobile', erster_eindruck: 'on first impression', passt_zu_mir: 'on “fits me”', glaubwuerdig: 'on “credible”', orientierung: 'on orientation',
};
const IN_LISTE_DE: Record<string, string> = {
  handy: '„Handy“', erster_eindruck: '„erster Eindruck“', passt_zu_mir: '„passt zu mir“', glaubwuerdig: '„seriös“', orientierung: '„Orientierung“',
};
const IN_LISTE_EN: Record<string, string> = {
  handy: '“mobile”', erster_eindruck: '“first impression”', passt_zu_mir: '“fits me”', glaubwuerdig: '“credible”', orientierung: '“orientation”',
};
const imSatzVon = (k: string) => t(IM_SATZ_DE, IM_SATZ_EN)[k] ?? k;
const inListeVon = (k: string) => t(IN_LISTE_DE, IN_LISTE_EN)[k] ?? k;

export interface LesartZeile { kriterium: string; label: string; heute: number; neu: number; abstand: number; richtung: Richtung }

export interface DesignLesart {
  zeilen: LesartZeile[];
  grenze: number;
  /** Personas je Check, wie im Bericht (null: unbekannt, gerechnet mit 8). */
  personas: { heute: number | null; neu: number | null };
  /** Die gemessene Streuung der Einzelnoten beider Checks (gepoolt), oder null: dann gilt die Annahme von einem Punkt. */
  streuung: number | null;
  /** Das Urteil als Überschrift, ohne Punkt, z. B. „Am Handy deutlich besser, sonst etwa gleichauf“. */
  satz: string;
  /** Die Lesart in Sätzen: was deutlich ist, was eine Tendenz, was im Rauschen liegt, wo die Grenzen liegen. */
  erklaerung: string;
  /** Etiketten neben der Überschrift: deutliche Unterschiede farbig, Tendenzen neutral. */
  marken: { text: string; ton: 'plus' | 'minus' | 'neutral' }[];
  /** Der größte deutliche Unterschied, sonst null (dann zeigt die Seite keine Pfeilzahl). */
  groesste: LesartZeile | null;
  /** Kein Kriterium schlechter (auch nicht als Tendenz), und mindestens die Hälfte deutlich besser. */
  bestanden: boolean;
  /** Ein Kriterium ist schlechter, deutlich oder als Tendenz: dann spricht der Beleg nicht nur für die neue Fassung. */
  dagegen: boolean;
}

const personasVon = (d: any): number | null => (typeof d?.personas === 'number' && d.personas >= 1 ? d.personas : null);
const istDeutlich = (r: Richtung) => r === 'deutlich-besser' || r === 'deutlich-schlechter';
const istTendenz = (r: Richtung) => r === 'besser' || r === 'schlechter';

/**
 * Die ehrliche Lesart zweier Design-Checks, heute gegen neu. Gerechnet, nicht
 * geschrieben: je Kriterium der Abstand, gegen die Rauschgrenze aus der Zahl
 * der Personas und der Streuung gelesen. Bis zur Grenze „etwa gleich“, ab der
 * doppelten „deutlich“, dazwischen eine Tendenz, die nie als „klar“ oder
 * „besser“ ohne Einschränkung dasteht (ein Abstand von 1,2 Standardfehlern ist
 * bei p ≈ 0,23 kein Befund). Nie „rundum besser“, wenn eine Note sinkt. Null,
 * wenn kein Kriterium in beiden Checks eine Note hat.
 */
export function designLesart(heute: any, neu: any): DesignLesart | null {
  const pH = personasVon(heute);
  const pN = personasVon(neu);
  const streuung = gepoolteStreuung(streuungVon(heute), streuungVon(neu));
  const grenze = rauschgrenze(pH, pN, streuung);
  const roh: LesartZeile[] = [];
  for (const k of KRITERIEN) {
    const a = heute?.noten?.[k];
    const b = neu?.noten?.[k];
    if (!istZahl(a) || !istZahl(b)) continue;
    const abstand = rund(b - a);
    roh.push({ kriterium: k, label: kriteriumKurz()[k] ?? k, heute: a, neu: b, abstand, richtung: richtung(abstand, grenze) });
  }
  if (!roh.length) return null;
  const nachAbstand = (a: LesartZeile, b: LesartZeile) => Math.abs(b.abstand) - Math.abs(a.abstand);
  const klar = roh.filter((z) => istDeutlich(z.richtung)).sort(nachAbstand);
  const tendenz = roh.filter((z) => istTendenz(z.richtung)).sort(nachAbstand);
  const gleich = roh.filter((z) => z.richtung === 'gleich');
  const zeilen = [...klar, ...tendenz, ...gleich];
  const je = (r: Richtung) => roh.filter((z) => z.richtung === r).map((z) => imSatzVon(z.kriterium));
  const imSatz = (z: LesartZeile) => imSatzVon(z.kriterium);
  if (sprache() === 'en') return designLesartEn({ roh, zeilen, klar, tendenz, gleich, je, imSatz, grenze, pH, pN, streuung });

  // Die Überschrift.
  let satz: string;
  if (!klar.length && !tendenz.length) {
    satz = 'Heute und neu etwa gleichauf';
  } else if (roh.length >= 3 && roh.every((z) => z.richtung === 'deutlich-besser')) {
    satz = `In allen ${zahlwort(roh.length).toLowerCase()} Kriterien besser`;
  } else if (roh.length >= 3 && roh.every((z) => z.richtung === 'deutlich-schlechter')) {
    satz = `In allen ${zahlwort(roh.length).toLowerCase()} Kriterien schlechter`;
  } else if (!klar.length) {
    // Nur Tendenzen: die Überschrift nennt die Richtung, welche Kriterien es
    // sind, sagen die Etiketten daneben und die Lesart darunter.
    const hoch = tendenz.some((z) => z.richtung === 'besser');
    const runter = tendenz.some((z) => z.richtung === 'schlechter');
    satz = hoch && runter ? 'Etwa gleichauf, mit Tendenzen in beide Richtungen'
      : hoch ? 'Etwa gleichauf, mit Tendenz nach oben' : 'Etwa gleichauf, mit Tendenz nach unten';
  } else {
    // Die Überschrift nennt nur, was deutlich ist; Tendenzen stehen in den
    // Etiketten und in der Lesart, sonst wird der Satz zur Aufzählung.
    const teile = [
      je('deutlich-besser').length ? `${aufzaehlung(je('deutlich-besser'))} deutlich besser` : '',
      je('deutlich-schlechter').length ? `${aufzaehlung(je('deutlich-schlechter'))} deutlich schlechter` : '',
      klar.length < roh.length ? 'sonst etwa gleichauf' : '',
    ].filter(Boolean);
    satz = gross(teile.join(', '));
  }

  // Die Lesart darunter.
  const saetze: string[] = [];
  const mitAbstand = (z: LesartZeile) => `${imSatz(z)} (${vorzeichen(z.abstand)})`;
  if (!klar.length && !tendenz.length) saetze.push('Kein Unterschied liegt über dem Rauschen.');
  if (klar.length) {
    if (klar.length === roh.length && roh.length > 1) saetze.push(`Alle Unterschiede sind deutlich: ${aufzaehlung(klar.map(mitAbstand))}.`);
    else if (klar.length === 1) saetze.push(roh.length > 1 ? `Deutlich ist nur der Unterschied ${mitAbstand(klar[0]!)}.` : `Der Unterschied ${mitAbstand(klar[0]!)} ist deutlich.`);
    else saetze.push(`Deutlich sind die Unterschiede ${aufzaehlung(klar.map(mitAbstand))}.`);
  }
  if (tendenz.length) {
    saetze.push(tendenz.length === 1
      ? `${gross(mitAbstand(tendenz[0]!))} liegt der Abstand über dem Rauschen, aber unter dem Doppelten: eine Tendenz, kein klarer Unterschied.`
      : `${gross(aufzaehlung(tendenz.map(mitAbstand)))} liegen die Abstände über dem Rauschen, aber unter dem Doppelten: Tendenzen, keine klaren Unterschiede.`);
  }
  const imRauschen = (zs: LesartZeile[], wort: string) => {
    if (!zs.length) return;
    const lo = Math.min(...zs.map((z) => Math.abs(z.abstand)));
    const hi = Math.max(...zs.map((z) => Math.abs(z.abstand)));
    const spanne = lo === hi ? zahl1(lo) : `${zahl1(lo)} bis ${zahl1(hi)}`;
    saetze.push(zs.length === 1
      ? `${gross(imSatz(zs[0]!))} liegt neu ${spanne} ${wort}.`
      : `Bei ${aufzaehlung(zs.map((z) => inListeVon(z.kriterium)))} liegt neu ${spanne} ${wort}.`);
  };
  imRauschen(gleich.filter((z) => z.abstand > 0), 'vorn');
  imRauschen(gleich.filter((z) => z.abstand < 0), 'hinten');
  const null0 = gleich.filter((z) => z.abstand === 0);
  if (null0.length) {
    saetze.push(null0.length === 1
      ? `${gross(imSatz(null0[0]!))} liegen beide gleich.`
      : `Bei ${aufzaehlung(null0.map((z) => inListeVon(z.kriterium)))} liegen beide gleich.`);
  }
  const wie = pH && pN ? (pH === pN ? `Bei ${pH} Personas je Check` : `Bei ${pH} und ${pN} Personas`) : `Gerechnet mit ${PERSONAS_VORGABE} Personas je Check`;
  const streu = streuung != null
    ? `einer gemessenen Streuung der Einzelnoten von ${zahl1(streuung)} Punkten`
    : 'einer angenommenen Streuung der Einzelnoten von einem Punkt';
  saetze.push(`${wie} und ${streu} ist ein Abstand bis ${zahl1(grenze)} Rauschen, ab ${zahl1(2 * grenze)} deutlich.`);

  const marken: DesignLesart['marken'] = [
    ...klar.map((z) => ({ text: `${z.label} ${vorzeichen(z.abstand)}`, ton: (z.abstand > 0 ? 'plus' : 'minus') as 'plus' | 'minus' })),
    ...tendenz.map((z) => ({ text: `${z.label} ${vorzeichen(z.abstand)}, Tendenz`, ton: 'neutral' as const })),
  ];
  if (marken.length && gleich.length) marken.push({ text: 'Rest etwa gleich', ton: 'neutral' });
  if (!marken.length) marken.push({ text: 'etwa gleich', ton: 'neutral' });

  return lesartErgebnis(roh, zeilen, klar, grenze, pH, pN, streuung, satz, saetze, marken);
}

function lesartErgebnis(
  roh: LesartZeile[], zeilen: LesartZeile[], klar: LesartZeile[], grenze: number, pH: number | null, pN: number | null,
  streuung: number | null, satz: string, saetze: string[], marken: DesignLesart['marken'],
): DesignLesart {
  const schlechter = roh.filter((z) => z.richtung === 'schlechter' || z.richtung === 'deutlich-schlechter');
  const deutlichBesser = roh.filter((z) => z.richtung === 'deutlich-besser');
  return {
    zeilen,
    grenze,
    personas: { heute: pH, neu: pN },
    streuung,
    satz,
    erklaerung: saetze.join(' '),
    marken,
    groesste: klar[0] ?? null,
    bestanden: !schlechter.length && deutlichBesser.length * 2 >= roh.length,
    dagegen: schlechter.length > 0,
  };
}

/** Die Lesart auf Englisch: dieselbe Rechnung, dieselben Fälle, andere Sätze. */
function designLesartEn(x: {
  roh: LesartZeile[]; zeilen: LesartZeile[]; klar: LesartZeile[]; tendenz: LesartZeile[]; gleich: LesartZeile[];
  je: (r: Richtung) => string[]; imSatz: (z: LesartZeile) => string; grenze: number; pH: number | null; pN: number | null; streuung: number | null;
}): DesignLesart {
  const { roh, zeilen, klar, tendenz, gleich, je, imSatz, grenze, pH, pN, streuung } = x;
  let satz: string;
  if (!klar.length && !tendenz.length) {
    satz = 'Current and new roughly level';
  } else if (roh.length >= 3 && roh.every((z) => z.richtung === 'deutlich-besser')) {
    satz = `Better on all ${zahlwort(roh.length).toLowerCase()} criteria`;
  } else if (roh.length >= 3 && roh.every((z) => z.richtung === 'deutlich-schlechter')) {
    satz = `Worse on all ${zahlwort(roh.length).toLowerCase()} criteria`;
  } else if (!klar.length) {
    const hoch = tendenz.some((z) => z.richtung === 'besser');
    const runter = tendenz.some((z) => z.richtung === 'schlechter');
    satz = hoch && runter ? 'Roughly level, with tendencies in both directions'
      : hoch ? 'Roughly level, with a tendency upward' : 'Roughly level, with a tendency downward';
  } else {
    const teile = [
      je('deutlich-besser').length ? `clearly better ${aufzaehlung(je('deutlich-besser'))}` : '',
      je('deutlich-schlechter').length ? `clearly worse ${aufzaehlung(je('deutlich-schlechter'))}` : '',
      klar.length < roh.length ? 'otherwise roughly level' : '',
    ].filter(Boolean);
    satz = gross(teile.join(', '));
  }

  const saetze: string[] = [];
  const mitAbstand = (z: LesartZeile) => `${imSatz(z)} (${vorzeichen(z.abstand)})`;
  if (!klar.length && !tendenz.length) saetze.push('No difference is above the noise.');
  if (klar.length) {
    if (klar.length === roh.length && roh.length > 1) saetze.push(`All differences are clear: ${aufzaehlung(klar.map(mitAbstand))}.`);
    else if (klar.length === 1) saetze.push(roh.length > 1 ? `Only the difference ${mitAbstand(klar[0]!)} is clear.` : `The difference ${mitAbstand(klar[0]!)} is clear.`);
    else saetze.push(`The differences ${aufzaehlung(klar.map(mitAbstand))} are clear.`);
  }
  if (tendenz.length) {
    saetze.push(tendenz.length === 1
      ? `${gross(mitAbstand(tendenz[0]!))}, the gap is above the noise but below double: a tendency, not a clear difference.`
      : `${gross(aufzaehlung(tendenz.map(mitAbstand)))}, the gaps are above the noise but below double: tendencies, not clear differences.`);
  }
  const imRauschen = (zs: LesartZeile[], wort: string) => {
    if (!zs.length) return;
    const lo = Math.min(...zs.map((z) => Math.abs(z.abstand)));
    const hi = Math.max(...zs.map((z) => Math.abs(z.abstand)));
    const spanne = lo === hi ? zahl1(lo) : `${zahl1(lo)} to ${zahl1(hi)}`;
    saetze.push(zs.length === 1
      ? `${gross(imSatz(zs[0]!))}, new is ${spanne} ${wort}.`
      : `On ${aufzaehlung(zs.map((z) => inListeVon(z.kriterium)))}, new is ${spanne} ${wort}.`);
  };
  imRauschen(gleich.filter((z) => z.abstand > 0), 'ahead');
  imRauschen(gleich.filter((z) => z.abstand < 0), 'behind');
  const null0 = gleich.filter((z) => z.abstand === 0);
  if (null0.length) {
    saetze.push(null0.length === 1
      ? `${gross(imSatz(null0[0]!))}, both are level.`
      : `On ${aufzaehlung(null0.map((z) => inListeVon(z.kriterium)))}, both are level.`);
  }
  const wie = pH && pN
    ? (pH === pN ? `With ${pH} ${pH === 1 ? 'persona' : 'personas'} per check` : `With ${pH} and ${pN} personas`)
    : `Calculated with ${PERSONAS_VORGABE} personas per check`;
  const streu = streuung != null
    ? `a measured spread of individual scores of ${zahl1(streuung)} points`
    : 'an assumed spread of individual scores of one point';
  saetze.push(`${wie} and ${streu}, a gap up to ${zahl1(grenze)} is noise, from ${zahl1(2 * grenze)} it is clear.`);

  const marken: DesignLesart['marken'] = [
    ...klar.map((z) => ({ text: `${z.label} ${vorzeichen(z.abstand)}`, ton: (z.abstand > 0 ? 'plus' : 'minus') as 'plus' | 'minus' })),
    ...tendenz.map((z) => ({ text: `${z.label} ${vorzeichen(z.abstand)}, tendency`, ton: 'neutral' as const })),
  ];
  if (marken.length && gleich.length) marken.push({ text: 'Rest roughly level', ton: 'neutral' });
  if (!marken.length) marken.push({ text: 'roughly level', ton: 'neutral' });

  return lesartErgebnis(roh, zeilen, klar, grenze, pH, pN, streuung, satz, saetze, marken);
}

/** „Note am Handy“, „Note beim ersten Eindruck“: die Zeile unter der großen Zahl. */
export const noteBei = (kriterium: string) => t(`Note ${imSatzVon(kriterium)}`, `Score ${imSatzVon(kriterium)}`);

// ─── Beleg 3: Fachprüfer ─────────────────────────────────────────────────────

export const BEREICH_KURZ: Record<string, string> = {
  hierarchie: 'Hierarchie', typografie: 'Typografie', layout: 'Layout', farbe: 'Farbe', bedienung: 'Bedienung',
  barrierefreiheit: 'Barrierefreiheit', einheitlichkeit: 'Einheitlichkeit', markentreue: 'Markentreue', handy: 'Handy', politur: 'Politur',
};
export const MARKER_TEXT: Record<string, string> = {
  gut: 'gut', teilweise: 'teilweise', fehlt: 'fehlt', nicht_noetig: 'nicht nötig', unklar: 'unklar',
};
export const BEREICH_KURZ_EN: Record<string, string> = {
  hierarchie: 'Hierarchy', typografie: 'Typography', layout: 'Layout', farbe: 'Color', bedienung: 'Controls',
  barrierefreiheit: 'Accessibility', einheitlichkeit: 'Consistency', markentreue: 'Brand fidelity', handy: 'Mobile', politur: 'Polish',
};
export const MARKER_TEXT_EN: Record<string, string> = {
  gut: 'good', teilweise: 'partial', fehlt: 'missing', nicht_noetig: 'not needed', unklar: 'unclear',
};
/** Marker in der aktuellen Sprache. */
export const markerText = (): Record<string, string> => t(MARKER_TEXT, MARKER_TEXT_EN);

export interface Befund { bereich: string; schwere: number; was: string; tun: string }

export interface FachprueferBeleg {
  grad: { 4: number; 3: number; 2: number };
  /** Grad 0 bis 1, nur gezählt. */
  kleinere: number;
  /** Grad 3 und 4 zusammen. */
  schwer: number;
  bereiche: { bereich: string; label: string; marker: string }[];
  gut: number;
  teilweise: number;
  /** Fehlt, unklar, nicht nötig. */
  uebrige: number;
  befundeSchwer: Befund[];
  befundeGrad2: Befund[];
  staerken: string[];
  gemessen: boolean;
  bestanden: boolean;
  titel: string;
  etikett: string;
  /** „5 von 9 Bereichen gut, 4 teilweise.“ Leer ohne Bereiche (ältere Checks). */
  bereicheSatz: string;
  /** „Drei Befunde stören (Grad 2).“ */
  grad2Satz: string;
}

/** Der Fachprüfer eines Checks (hier: der neuen Fassung), oder null. */
export function fachprueferBeleg(d: any): FachprueferBeleg | null {
  const h = d?.handwerk;
  if (!h || typeof h !== 'object') return null;
  const befunde: Befund[] = (Array.isArray(h.befunde) ? h.befunde : [])
    .filter((b: any) => istZahl(b?.schwere) && text(b?.was))
    .map((b: any) => ({ bereich: text(b.bereich), schwere: b.schwere, was: text(b.was), tun: text(b.tun) }));
  const grad = { 4: befunde.filter((b) => b.schwere >= 4).length, 3: befunde.filter((b) => b.schwere === 3).length, 2: befunde.filter((b) => b.schwere === 2).length };
  const schwer = grad[4] + grad[3];
  const bereiche = (Array.isArray(h.bereiche) ? h.bereiche : [])
    .filter((b: any) => text(b?.bereich))
    .map((b: any) => ({ bereich: text(b.bereich), label: t(BEREICH_KURZ, BEREICH_KURZ_EN)[text(b.bereich)] ?? text(b.bereich), marker: text(b.marker) || 'unklar' }));
  const gut = bereiche.filter((b: any) => b.marker === 'gut').length;
  const teilweise = bereiche.filter((b: any) => b.marker === 'teilweise').length;
  const uebrige = bereiche.length - gut - teilweise;
  const en = sprache() === 'en';
  const bereicheSatz = !bereiche.length ? ''
    : en
      ? `${gut} of ${bereiche.length} areas good${teilweise ? `, ${teilweise} partial` : ''}${uebrige ? `, ${uebrige} ${uebrige === 1 ? 'other is' : 'others are'} missing or unclear` : ''}.`
      : `${gut} von ${bereiche.length} Bereichen gut${teilweise ? `, ${teilweise} teilweise` : ''}${uebrige ? `, ${uebrige} ${uebrige === 1 ? 'weiterer' : 'weitere'} fehlt oder unklar` : ''}.`;
  const grad2Satz = en
    ? (grad[2] === 0 ? 'No finding gets in the way (grade 2).' : `${zahlwort(grad[2])} ${grad[2] === 1 ? 'finding gets' : 'findings get'} in the way (grade 2).`)
    : grad[2] === 0 ? 'Kein Befund stört (Grad 2).' : `${zahlwort(grad[2])} ${grad[2] === 1 ? 'Befund stört' : 'Befunde stören'} (Grad 2).`;
  return {
    grad,
    kleinere: ganz(h.kleinere),
    schwer,
    bereiche,
    gut,
    teilweise,
    uebrige,
    befundeSchwer: befunde.filter((b) => b.schwere >= 3).sort((a, b) => b.schwere - a.schwere),
    befundeGrad2: befunde.filter((b) => b.schwere === 2),
    staerken: (Array.isArray(h.staerken) ? h.staerken : []).map(text).filter(Boolean).slice(0, 3),
    gemessen: h.gemessen === true,
    bestanden: schwer === 0,
    titel: en
      ? (schwer === 0 ? 'No serious finding in the design' : schwer === 1 ? 'One serious finding in the design' : `${zahlwort(schwer)} serious findings in the design`)
      : schwer === 0 ? 'Kein schwerer Befund im Design' : schwer === 1 ? 'Ein schwerer Befund im Design' : `${zahlwort(schwer)} schwere Befunde im Design`,
    etikett: schwer === 0 ? t('kein Grad 3 oder 4', 'no grade 3 or 4') : t(`${schwer} mit Grad 3 oder 4`, `${schwer} with grade 3 or 4`),
    bereicheSatz,
    grad2Satz,
  };
}

// ─── Aufbau der Seite ────────────────────────────────────────────────────────

export type BelegArt = 'text' | 'design' | 'fachpruefer';

export interface Belege {
  text: TextBeleg | null;
  /**
   * Mit zwei vergleichbaren Checks die Lesart; mit nur dem neuen, oder wenn
   * heute und neu verschiedene Zielgruppen hatten, die Noten der neuen Fassung
   * (lesart null, und `unvergleichbar` sagt warum).
   */
  design: { lesart: DesignLesart | null; neu: any; heute: any | null; unvergleichbar: string | null; jury: string | null } | null;
  fachpruefer: FachprueferBeleg | null;
  /** Die vorhandenen Belege in fester Reihenfolge, mit Nummer. */
  reihe: { art: BelegArt; nr: number }[];
}

/**
 * Hatten heute und neu dieselbe Jury? Zwei verschiedene Zielgruppen machen den
 * Abstand systematisch, nicht zum Rauschen: dann keine Lesart, sondern ein
 * Satz, warum (`unvergleichbar`). Dieselbe Zielgruppe mit nicht ganz denselben
 * Personas (etwa nach einer Überarbeitung) bleibt lesbar, mit einem Hinweis
 * (`jury`). Beides friert publish_report ein (bericht_vorschlag.ts); ältere
 * Berichte haben es nicht, dann gilt die Lesart wie bisher.
 */
function juryVergleich(data: any): { unvergleichbar: string | null; jury: string | null } {
  const j = data?.design_vergleich?.jury;
  const zH = text(j?.zielgruppen?.heute);
  const zN = text(j?.zielgruppen?.neu);
  if (zH && zN) {
    return {
      unvergleichbar: zH === zN
        ? t('Heute und neu urteilten zwei verschiedene Zielgruppen gleichen Namens. Die Noten sind deshalb nicht vergleichbar und stehen ohne Lesart.',
          'Current and new were judged by two different audiences with the same name. The scores are therefore not comparable and appear without a reading.')
        : t(`Heute urteilte die Zielgruppe „${zH}“, neu die Zielgruppe „${zN}“. Zwei verschiedene Jurys: die Noten sind deshalb nicht vergleichbar und stehen ohne Lesart.`,
          `Current was judged by the audience “${zH}”, new by the audience “${zN}”. Two different juries: the scores are therefore not comparable and appear without a reading.`),
      jury: null,
    };
  }
  const gemeinsam = ganz(j?.gemeinsam);
  const nH = ganz(j?.heute);
  const nN = ganz(j?.neu);
  if (j && nH && nN && gemeinsam < Math.max(nH, nN)) {
    return {
      unvergleichbar: null,
      jury: gemeinsam
        ? t(`Nicht ganz dieselbe Jury: ${gemeinsam} ${gemeinsam === 1 ? 'Persona urteilte' : 'Personas urteilten'} in beiden Checks, die übrigen nur in einem. Ein Teil des Abstands kann daher kommen.`,
          `Not quite the same jury: ${gemeinsam} ${gemeinsam === 1 ? 'persona' : 'personas'} judged in both checks, the others in only one. Part of the gap may come from that.`)
        : t('Keine Persona urteilte in beiden Checks: ein Teil des Abstands kann daher kommen.', 'No persona judged in both checks: part of the gap may come from that.'),
    };
  }
  return { unvergleichbar: null, jury: null };
}

/** Die drei Belege, soweit die Nutzlast sie trägt. Reihenfolge immer Text, Design, Fachprüfer. */
export function belegeDes(data: any): Belege {
  const tb = textBeleg(data);
  const { heute, neu } = vergleichsChecks(data);
  const { unvergleichbar, jury } = heute && neu ? juryVergleich(data) : { unvergleichbar: null, jury: null };
  const lesart = heute && neu && !unvergleichbar ? designLesart(heute, neu) : null;
  const neuHatNoten = !!neu && KRITERIEN.some((k) => istZahl(neu?.noten?.[k]));
  const design = lesart ? { lesart, neu, heute, unvergleichbar: null, jury }
    : neuHatNoten ? { lesart: null, neu, heute: unvergleichbar ? heute : null, unvergleichbar, jury: null } : null;
  const f = neu ? fachprueferBeleg(neu) : null;
  const arten: BelegArt[] = [tb ? 'text' : null, design ? 'design' : null, f ? 'fachpruefer' : null].filter(Boolean) as BelegArt[];
  return { text: tb, design, fachpruefer: f, reihe: arten.map((art, i) => ({ art, nr: i + 1 })) };
}

/**
 * Spricht kein Beleg gegen die neue Fassung? Dann heißt die Überschrift über
 * den Belegen „Was für diese Fassung spricht“ (wie im Entwurf), sonst „Was die
 * Prüfungen zeigen“. Nicht dasselbe wie „alle bestanden“: „am Handy deutlich
 * besser, sonst etwa gleichauf“ besteht nicht (zu wenig deutlich besser),
 * spricht aber auch nicht dagegen.
 */
export function keinerDagegen(b: Belege): boolean {
  if (b.text?.dagegen) return false;
  if (b.design?.lesart?.dagegen) return false;
  if (b.fachpruefer && b.fachpruefer.schwer > 0) return false;
  return b.reihe.length > 0;
}

export const BELEG_ANKER: Record<BelegArt, string> = { text: 'beleg-text', design: 'beleg-design', fachpruefer: 'beleg-fachpruefer' };
export const BELEG_NAME: Record<BelegArt, string> = { text: 'Text-Simulation', design: 'Design-Simulation', fachpruefer: 'Fachprüfer' };
export const BELEG_NAME_EN: Record<BelegArt, string> = { text: 'Text simulation', design: 'Design simulation', fachpruefer: 'Expert reviewer' };
/** Name eines Belegs in der aktuellen Sprache. */
export const belegName = (art: BelegArt) => t(BELEG_NAME, BELEG_NAME_EN)[art];

export interface Sprungziel { id: string; label: string }

/**
 * Die Sprunglinks „Auf dieser Seite“: nur zu Abschnitten, die es gibt. „Details“
 * gibt es immer (die Akkordeons), „Vorher und nachher“ nur mit Bildern oder
 * Änderungen.
 */
export function sprungziele(vorherNachher: boolean, belege: Belege): Sprungziel[] {
  return [
    ...(vorherNachher ? [{ id: 'vorher-nachher', label: t('Vorher und nachher', 'Before and after') }] : []),
    ...(belege.reihe.length ? [{ id: 'belege', label: t('Die Belege', 'The evidence') }] : []),
    ...belege.reihe.map((b) => ({ id: BELEG_ANKER[b.art], label: belegName(b.art) })),
    { id: 'details', label: t('Details', 'Details') },
  ];
}

/** Gibt es „Vorher und nachher“? Mit Bildern der neuen Fassung oder mit Änderungen. */
export function mitVorherNachher(v: Vorschlag, bilder: { heute: Seitenbilder | null; neu: Seitenbilder | null }): boolean {
  return v.aenderungen.length > 0 || !!bilder.neu || !!bilder.heute;
}

/**
 * Die Design-Checks für „Design-Check im Detail“: erst heute und neu aus dem
 * Vergleich, dann die Checks der Studie, die nicht schon dabei sind.
 */
export function designImDetail(data: any): { titel: string; d: any }[] {
  const { heute, neu } = vergleichsChecks(data);
  const gleich = (a: any, b: any) => a && b && a.url === b.url && a.am === b.am;
  const out: { titel: string; d: any }[] = [];
  if (heute) out.push({ titel: t('Die heutige Seite', 'The current page'), d: heute });
  if (neu) out.push({ titel: t('Die neue Fassung', 'The new version'), d: neu });
  for (const d of Array.isArray(data?.design) ? data.design : []) {
    if (!out.some((x) => gleich(x.d, d))) out.push({ titel: out.length ? t('Weiterer Design-Check', 'Another design check') : t('Design-Check', 'Design check'), d });
  }
  return out;
}
